import React, { FC } from 'react';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import {
  DropdownDefinition,
  QUIZ_TEXT_FILL
} from '~/training/sequence/templates/items';
import styled from '@xstyled/styled-components';
import {
  QuizDropdownProvider,
  useQuizDropdownStore
} from '~/training/sequence/templates/items/quiz/QuizDropdownStore';
import { Card } from '~/ui/card/Card';
import { t } from 'ttag';

interface Props {
  text?: string;
  dropdownItems?: DropdownDefinition;
}

const FlexBlock = styled.divBox`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding-top: 10px;
  gap: md;
  & div {
    display: inline;
    position: relative;
    float: left;
    left: 0px;
  }
  & p {
    display: inline;
    position: relative;
    float: left;
    left: 0px;
  }
  & button {
    display: inline;
    position: relative;
    float: left;
    left: 0px;
  }
`;

/** Visually hidden but kept in the a11y tree (same pattern as ComboButton). */
const StatusLive = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export type QuizTextFillDefinition = ItemDefinition<
  typeof QUIZ_TEXT_FILL,
  Props
>;

const STATUS_MESSAGE = () =>
  t`In order to continue, please select the correct answer(s) and remove any incorrect answer(s) by clicking on them on desktop or tapping on mobile or tablet.`;

const AddItemsToTheStore: FC<Props> = props => {
  const { pushItem, items } = useQuizDropdownStore();

  if (props.dropdownItems && items.length == 0) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    props.dropdownItems.forEach((item: DropdownDefinition) => pushItem(item));
  }

  const showMessage = Boolean(items.find(item => item.used));
  const statusMessage = showMessage ? STATUS_MESSAGE() : '';

  return (
    <>
      {/*
        CAT-1226 / WCAG 4.1.3: live region must exist on first paint (empty),
        then receive text. Mounting aria-live together with the message often
        skips screen-reader announcements.
      */}
      <StatusLive role="status" aria-live="polite" aria-atomic="true">
        {statusMessage}
      </StatusLive>
      {showMessage ? (
        <Card p="md" m="xxl" aria-hidden="true">
          {statusMessage}
        </Card>
      ) : null}
    </>
  );
};

export class QuizTextFill extends Item<typeof QUIZ_TEXT_FILL, Props> {
  protected render({ children, dropdownItems }: RenderProps<Props>) {
    return (
      <QuizDropdownProvider>
        <FlexBlock>{children}</FlexBlock>
        <AddItemsToTheStore dropdownItems={dropdownItems} />
      </QuizDropdownProvider>
    );
  }
}

registry[QUIZ_TEXT_FILL] = QuizTextFill;
